import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, dashboardService } from '../services/auth';
import MerchantHeader from '../components/MerchantHeader';
import { CiScoreBadge } from '../components/CiScoreBadge';
import { Send, CheckCircle, Clock, AlertCircle, Globe, Mail, MessageCircle, Hash } from 'lucide-react';
import { toast } from 'sonner';

export default function DistributionEnginePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [distributing, setDistributing] = useState(false);

  // Distribution channels
  // F-M04-7 (fix): the original list showed unsourced reach numbers and "Connected"/"Pending" states, but this page
  // calls no distribution backend and One-Click Push is "Coming Soon". Nothing here is measured or connected.
  // Original values, restore only with a real source (e.g. GET /api/merchant/distribution/channels/status + audience counts):
  //   web: status 'active', reach '50K+ users'; email: status 'active', reach '12K subscribers';
  //   telegram: status 'pending', reach '3K members'; social: status 'coming', reach 'Coming Soon'
  const channels = [
    { id: 'web', name: 'CI Deals Website', icon: Globe, status: 'coming', reach: null },
    { id: 'email', name: 'Email Subscribers', icon: Mail, status: 'coming', reach: null },
    { id: 'telegram', name: 'Telegram Channel', icon: MessageCircle, status: 'coming', reach: null },
    { id: 'social', name: 'Social Media', icon: Hash, status: 'coming', reach: null },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileData, dealsData] = await Promise.all([
        authService.getProfile(),
        dashboardService.getDeals()
      ]);
      setUser(profileData.member);
      setDeals(dealsData.deals || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    sessionStorage.clear();
    navigate('/login');
  };

  const toggleDealSelection = (dealId) => {
    setSelectedDeals(prev => 
      prev.includes(dealId) 
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const handleOneClickPush = () => {
    toast.info('Coming Soon', {
      description: 'One-Click Push distribution will be available soon!',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading distribution engine...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <MerchantHeader user={user} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Distribution Engine</h1>
          <p className="text-[#9CB0C7]">Push your deals across multiple channels with one click</p>
        </div>

        {/* Distribution Info Banner */}
        <div className="rounded-lg border border-[#3EF1C7]/30 bg-[#3EF1C7]/5 p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#3EF1C7]/10 flex items-center justify-center flex-shrink-0">
              <Send className="w-6 h-6 text-[#3EF1C7]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Multi-Channel Distribution</h3>
              <p className="text-[#9CB0C7] mb-4">
                {/* F-M04-7: original copy claimed "Reach thousands of deal-seekers" with no source. Restore only with measured audience data. */}
                Multi-channel distribution is not available from this page yet. Channels below are planned and are not connected here.
              </p>
              <div className="flex flex-wrap gap-4">
                {channels.map(channel => (
                  <div key={channel.id} className="flex items-center gap-2">
                    <channel.icon className={`w-4 h-4 ${
                      channel.status === 'active' ? 'text-[#8CF78E]' :
                      channel.status === 'pending' ? 'text-[#FFC857]' : 'text-[#9CB0C7]'
                    }`} />
                    <span className="text-sm text-[#9CB0C7]">{channel.name}</span>
                    {channel.status === 'active' && <CheckCircle className="w-3 h-3 text-[#8CF78E]" />}
                    {channel.status === 'pending' && <Clock className="w-3 h-3 text-[#FFC857]" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Distribution Channels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {channels.map(channel => (
            <div 
              key={channel.id}
              className={`rounded-lg border p-4 ${
                channel.status === 'active' 
                  ? 'border-[#8CF78E]/30 bg-[#8CF78E]/5' 
                  : channel.status === 'pending'
                  ? 'border-[#FFC857]/30 bg-[#FFC857]/5'
                  : 'border-[#22304A] bg-[#0E1426]/80'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <channel.icon className={`w-5 h-5 ${
                  channel.status === 'active' ? 'text-[#8CF78E]' :
                  channel.status === 'pending' ? 'text-[#FFC857]' : 'text-[#9CB0C7]'
                }`} />
                <span className="font-medium text-white">{channel.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#9CB0C7]">{channel.reach ?? 'Reach not measured'}</span>
                <span className={`px-2 py-0.5 text-xs rounded ${
                  channel.status === 'active' ? 'bg-[#8CF78E]/10 text-[#8CF78E]' :
                  channel.status === 'pending' ? 'bg-[#FFC857]/10 text-[#FFC857]' : 'bg-[#22304A] text-[#9CB0C7]'
                }`}>
                  {channel.status === 'active' ? 'Connected' : channel.status === 'pending' ? 'Pending' : 'Coming Soon'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Deals Selection */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden">
          <div className="p-6 border-b border-[#22304A] flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Select Deals to Distribute</h3>
              <p className="text-sm text-[#9CB0C7]">{selectedDeals.length} deals selected</p>
            </div>
            <button
              onClick={handleOneClickPush}
              disabled={selectedDeals.length === 0}
              className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                selectedDeals.length > 0
                  ? 'bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white hover:opacity-90'
                  : 'bg-[#22304A] text-[#9CB0C7] cursor-not-allowed'
              }`}
              data-testid="one-click-push-button"
            >
              <Send className="w-5 h-5" />
              One Click Push
            </button>
          </div>

          {deals.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-[#9CB0C7] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No deals available</h3>
              <p className="text-[#9CB0C7] mb-4">Create some deals first to distribute them</p>
              <button
                onClick={() => navigate('/dashboard/deals')}
                className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90"
              >
                Go to Deals
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[#22304A]">
              {deals.map(deal => (
                <div 
                  key={deal._id}
                  className={`p-4 flex items-center gap-4 cursor-pointer transition-colors ${
                    selectedDeals.includes(deal._id) ? 'bg-[#7A5CFF]/10' : 'hover:bg-[#111A2E]/40'
                  }`}
                  onClick={() => toggleDealSelection(deal._id)}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedDeals.includes(deal._id) 
                      ? 'border-[#7A5CFF] bg-[#7A5CFF]' 
                      : 'border-[#22304A]'
                  }`}>
                    {selectedDeals.includes(deal._id) && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <p className="font-medium text-white">{deal.title || `Deal ${deal.deal_id?.slice(0, 8)}`}</p>
                    <p className="text-sm text-[#9CB0C7]">
                      {deal.discount_type === 'percentage' ? `${deal.discount_value}% OFF` : `$${deal.discount_value} OFF`}
                    </p>
                  </div>
                  
                  <CiScoreBadge score={deal.ci_deal_score} />
                  
                  <div className="text-right">
                    <p className="text-sm text-[#9CB0C7]">{deal.views || 0} views</p>
                    <p className="text-sm text-[#3EF1C7]">{deal.clicks || 0} clicks</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
